import { Home, CreditCard, Library, LogOut, Film, ImageIcon, Shield, Menu, X, HelpCircle, BookOpen, Headphones } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to check if we're in preview environment
// AUDIOBOOK DISABLED FOR PRODUCTION - not production ready, still timing out
const isPreviewEnvironment = () => {
  const hostname = window.location.hostname;
  // Preview environments use emergent preview URLs or localhost
  return hostname.includes('preview') || 
         hostname.includes('localhost') || 
         hostname.includes('127.0.0.1') ||
         hostname.includes('emergent');
};

const Sidebar = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAudiobook, setShowAudiobook] = useState(false);

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsAdmin(response.data?.is_admin || false);
        }
      } catch (error) {
        console.error("Failed to check admin status:", error);
      }
    };
    checkAdmin();
    // Check if we're in preview environment
    setShowAudiobook(isPreviewEnvironment());
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const menuItems = [
    { path: "/dashboard", label: "Dashboard", icon: Home },
    { path: "/series", label: "Multi-Book Series", icon: Library },
    { path: "/cover-creator", label: "Cover Creation", icon: ImageIcon },
    { path: "/ebook-creator", label: "Ebook Formatter", icon: BookOpen },
    ...(showAudiobook ? [{ path: "/audiobook-creator", label: "Audiobook Creator", icon: Headphones }] : []),
    { path: "/script-creator", label: "Script Creator", icon: Film },
    { path: "/credits", label: "Get Credits", icon: CreditCard },
    { path: "/help", label: "Help Center", icon: HelpCircle }
  ];

  const isActive = (path) => location.pathname === path;

  const SidebarContent = () => (
    <>
      {/* Logo/Brand */}
      <div className="p-6 border-b border-stone-200 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-stone-900">DTR</h2>
          <p className="text-sm text-stone-600">Dream To Reality</p>
        </div>
        {/* Close button on mobile */}
        <Button 
          variant="ghost" 
          size="sm" 
          className="md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Menu Items */}
      <nav className="flex-1 p-4 space-y-2">
        {menuItems.map((item) => {
          const Icon = item.icon;
          return (
            <Button
              key={item.path}
              variant={isActive(item.path) ? "default" : "ghost"}
              className="w-full justify-start"
              onClick={() => navigate(item.path)}
            >
              <Icon className="h-5 w-5 mr-3" />
              {item.label}
            </Button>
          );
        })}
        
        {/* Admin Portal - Only visible to admins */}
        {isAdmin && (
          <Button
            variant={isActive("/admin") ? "default" : "ghost"}
            className={`w-full justify-start ${isActive("/admin") ? "" : "text-purple-700 hover:text-purple-800 hover:bg-purple-50"}`}
            onClick={() => navigate("/admin")}
          >
            <Shield className="h-5 w-5 mr-3" />
            Admin Portal
          </Button>
        )}
      </nav>

      {/* Support Email */}
      <div className="px-4 py-3 border-t border-stone-200">
        <p className="text-xs text-stone-500 mb-1">Need help?</p>
        <p 
          className="text-sm text-stone-700 font-medium select-all cursor-pointer hover:text-amber-700"
          title="Click to select email"
        >
          Support@dtrcreative.com
        </p>
      </div>

      {/* Logout */}
      <div className="p-4 border-t border-stone-200">
        <Button
          variant="ghost"
          className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={onLogout}
        >
          <LogOut className="h-5 w-5 mr-3" />
          Logout
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      <Button
        variant="outline"
        size="sm"
        className="fixed top-4 left-4 z-50 md:hidden bg-white shadow-md"
        onClick={() => setIsMobileMenuOpen(true)}
      >
        <Menu className="h-5 w-5" />
      </Button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div className={`fixed left-0 top-0 h-screen w-64 bg-white border-r border-stone-200 flex flex-col z-50 transform transition-transform duration-300 md:hidden ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <SidebarContent />
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden md:flex fixed left-0 top-0 h-screen w-64 bg-white border-r border-stone-200 flex-col">
        <SidebarContent />
      </div>
    </>
  );
};

export default Sidebar;
